import { CatalogArtworkRepository } from '@lib/api/repository/catalog-artwork/catalog-artwork.repository';
import { AdminCardArtwork, AdminCardArtworks, AdminCardPrintingArtwork, ArtworkManifestImportResult } from '@lib/types';

import { Requester } from '../clients';

interface AdminCardArtworksDTO {
  items: AdminCardArtwork[];
  printings: AdminCardPrintingArtwork[];
}

export default class CatalogArtworkAPI implements CatalogArtworkRepository {
  async exportArtworkManifest(signal?: AbortSignal): Promise<Blob> {
    return (
      await Requester.download(
        '/marketplace/admin/catalog/artworks/export-manifest', { signal },
      )
    ).data;
  }

  async importArtworkManifest(
    file: File,
  ): Promise<ArtworkManifestImportResult> {
    const body = new FormData();

    body.append('manifest', file);

    return (
      await Requester.post<ArtworkManifestImportResult>(
        '/marketplace/admin/catalog/artworks/import-manifest',
        { body },
      )
    ).data;
  }

  async listArtworks(
    cardId: string,
    collectionId?: string, signal?: AbortSignal,
  ): Promise<AdminCardArtworks> {
    return (
      await Requester.get<AdminCardArtworksDTO>(
        `/marketplace/admin/catalog/${cardId}/artworks`,
        {signal,  params: collectionId ? { collection_id: collectionId } : undefined },
      )
    ).data;
  }

  async setArtworkExternalReference(cardId: string, externalId: string) {
    await Requester.put(
      `/marketplace/admin/catalog/${cardId}/artworks/ygoresources-reference`,
      { body: { externalId } },
    );
  }

  async synchronizeArtworks(cardId: string): Promise<AdminCardArtwork[]> {
    return (
      await Requester.post<AdminCardArtworksDTO>(
        `/marketplace/admin/catalog/${cardId}/artworks/synchronize`,
      )
    ).data.items;
  }

  async uploadArtwork(
    cardId: string,
    file: File,
    isPrimary?: boolean,
  ): Promise<AdminCardArtworks> {
    const body = new FormData();

    body.append('file', file);

    if (isPrimary !== undefined) {
      body.append('isPrimary', String(isPrimary));
    }

    return (
      await Requester.post<AdminCardArtworksDTO>(
        `/marketplace/admin/catalog/${cardId}/artworks/upload`,
        { body },
      )
    ).data;
  }

  async setPrimaryArtwork(
    cardId: string,
    artworkId: string,
  ): Promise<AdminCardArtwork[]> {
    return (
      await Requester.post<AdminCardArtworksDTO>(
        `/marketplace/admin/catalog/${cardId}/artworks/${artworkId}/primary`,
      )
    ).data.items;
  }

  async setArtworkEnabled(
    cardId: string,
    artworkId: string,
    enabled: boolean,
  ): Promise<AdminCardArtwork[]> {
    return (
      await Requester.patch<AdminCardArtworksDTO>(
        `/marketplace/admin/catalog/${cardId}/artworks/${artworkId}/status`,
        { body: { enabled } },
      )
    ).data.items;
  }

  async deleteArtwork(
    cardId: string,
    artworkId: string,
  ): Promise<AdminCardArtwork[]> {
    return (
      await Requester.delete<AdminCardArtworksDTO>(
        `/marketplace/admin/catalog/${cardId}/artworks/${artworkId}`,
      )
    ).data.items;
  }

  async setPrintingArtwork(
    cardId: string,
    printingId: string,
    options: {
      artworkId?: string | null;
      rarityId?: string;
      code?: string;
      isAlternateArt?: boolean;
    },
  ): Promise<AdminCardPrintingArtwork> {
    return (
      await Requester.put<AdminCardPrintingArtwork>(
        `/marketplace/admin/catalog/${cardId}/printings/${printingId}/artwork`,
        {
          body: {
            artworkId: options.artworkId,
            rarityId: options.rarityId,
            code: options.code,
            is_alternate_art: options.isAlternateArt,
          },
        },
      )
    ).data;
  }

  async createPrinting(
    cardId: string,
    data: {
      collectionId: string;
      rarityId: string;
      code: string;
      isAlternateArt?: boolean;
      artworkId?: string | null;
    },
  ): Promise<AdminCardPrintingArtwork> {
    return (
      await Requester.post<AdminCardPrintingArtwork>(
        `/marketplace/admin/catalog/${cardId}/printings`,
        {
          body: {
            collectionId: data.collectionId,
            rarityId: data.rarityId,
            code: data.code,
            is_alternate_art: data.isAlternateArt,
            artworkId: data.artworkId,
          },
        },
      )
    ).data;
  }

  async deletePrinting(cardId: string, printingId: string): Promise<void> {
    await Requester.delete(
      `/marketplace/admin/catalog/${cardId}/printings/${printingId}`,
    );
  }

  async reorderPrintings(
    cardId: string,
    printingIds: string[],
  ): Promise<AdminCardArtworks> {
    return (
      await Requester.put<AdminCardArtworks>(
        `/marketplace/admin/catalog/${cardId}/printings/reorder`,
        { body: { printing_ids: printingIds } },
      )
    ).data;
  }
}
